/**
 * DrawPredictionOverlay for Poker Reborn
 *
 * Shows draw predictions (flush draw, OESD, gutshot) as a semi-transparent
 * overlay on empty community card slots (turn/river).
 *
 * @see docs/screens/room/DRAW_PREDICTION.md - Visual specs, behavior, architecture
 * @see tasks/active/T71_DRAW_PREDICTION_OVERLAY.md - Original task spec
 * @see src/utils/drawDetection.ts - Draw detection algorithm
 */

import { Container, Graphics, Text } from 'pixi.js';

import type { DrawInfo } from '@/utils/drawDetection';

/** Suit display glyphs and colors */
const SUIT_DISPLAY: Record<string, { glyph: string; color: number }> = {
  s: { glyph: '\u2660', color: 0x222222 },
  h: { glyph: '\u2665', color: 0xcc0000 },
  d: { glyph: '\u2666', color: 0xcc0000 },
  c: { glyph: '\u2663', color: 0x222222 },
};

/** Design dimensions — matches CARD_STYLES['large'] */
const OVERLAY_WIDTH = 90;
const OVERLAY_HEIGHT = 126;
const CORNER_RADIUS = 8;

export class DrawPredictionOverlay extends Container {
  private background: Graphics;
  private drawContainers: Container[] = [];

  constructor() {
    super();
    this.background = new Graphics();
    this.addChild(this.background);
    // Center overlay on the card slot position (CardView uses anchor 0.5)
    this.pivot.set(OVERLAY_WIDTH / 2, OVERLAY_HEIGHT / 2);
    this.visible = false;
  }

  /** Update overlay to show 1-2 draws, or hide if empty */
  setDraws(draws: DrawInfo[]): void {
    // Clean up previous draw displays
    for (const container of this.drawContainers) {
      container.destroy({ children: true });
    }
    this.drawContainers = [];

    if (draws.length === 0) {
      this.visible = false;
      return;
    }

    this.drawBackground(draws);
    this.renderDraws(draws);
    this.visible = true;
  }

  private drawBackground(draws: DrawInfo[]): void {
    const backgroundGraphics = this.background;
    backgroundGraphics.clear();

    // Dark background with rounded corners
    backgroundGraphics.roundRect(0, 0, OVERLAY_WIDTH, OVERLAY_HEIGHT, CORNER_RADIUS);
    backgroundGraphics.fill({ color: 0x1a1a2e, alpha: 0.88 });

    // Dashed border effect (solid thin border for simplicity in PixiJS)
    backgroundGraphics.roundRect(0, 0, OVERLAY_WIDTH, OVERLAY_HEIGHT, CORNER_RADIUS);
    backgroundGraphics.stroke({ color: 0x444466, width: 1, alpha: 0.6 });

    // Suit-colored accent line at top for flush draws
    const flushDraw = draws.find((d) => d.suitColor != null);
    if (flushDraw?.suitColor != null) {
      const suitInfo = SUIT_DISPLAY[flushDraw.suitColor];
      if (suitInfo) {
        backgroundGraphics.roundRect(2, 2, OVERLAY_WIDTH - 4, 3, 1);
        backgroundGraphics.fill({ color: suitInfo.color, alpha: 0.8 });
      }
    }
  }

  private renderDraws(draws: DrawInfo[]): void {
    const isSingle = draws.length === 1;
    const slotHeight = isSingle ? OVERLAY_HEIGHT : OVERLAY_HEIGHT / 2;

    for (let i = 0; i < draws.length; i++) {
      const draw = draws[i]!;
      const container = new Container();
      container.y = i * slotHeight;

      this.renderSingleDraw(container, draw, slotHeight, i === 0);
      this.addChild(container);
      this.drawContainers.push(container);
    }

    // Divider between draws
    if (draws.length === 2) {
      const divider = new Graphics();
      divider.moveTo(8, OVERLAY_HEIGHT / 2);
      divider.lineTo(OVERLAY_WIDTH - 8, OVERLAY_HEIGHT / 2);
      divider.stroke({ color: 0x666688, width: 0.5, alpha: 0.5 });
      this.addChild(divider);
      this.drawContainers.push(divider as unknown as Container);
    }
  }

  private renderSingleDraw(
    container: Container,
    draw: DrawInfo,
    slotHeight: number,
    isFirst: boolean,
  ): void {
    const centerX = OVERLAY_WIDTH / 2;
    const topPadding = isFirst ? 10 : 4;
    let currentY = topPadding;

    // Draw name
    const nameText = new Text({
      text: draw.drawName,
      style: {
        fontFamily: 'Arial, sans-serif',
        fontSize: 11,
        fontWeight: 'bold',
        fill: 0xffffff,
        align: 'center',
      },
    });
    nameText.anchor.set(0.5, 0);
    nameText.x = centerX;
    nameText.y = currentY;
    container.addChild(nameText);
    currentY += 14;

    // Outs count
    const outsText = new Text({
      text: `${String(draw.outs)} outs`,
      style: {
        fontFamily: 'Arial, sans-serif',
        fontSize: 9,
        fill: 0xffd700,
        align: 'center',
      },
    });
    outsText.anchor.set(0.5, 0);
    outsText.x = centerX;
    outsText.y = currentY;
    container.addChild(outsText);
    currentY += 14;

    // Mini card icons — show up to 8 needed cards
    const maxCards = Math.min(draw.neededCards.length, 8);
    const cardsPerRow = 4;
    const cardSpacing = 18;
    const rows = Math.ceil(maxCards / cardsPerRow);

    for (let row = 0; row < rows; row++) {
      const rowStart = row * cardsPerRow;
      const rowEnd = Math.min(rowStart + cardsPerRow, maxCards);
      const rowCount = rowEnd - rowStart;
      const rowWidth = rowCount * cardSpacing;
      const startX = centerX - rowWidth / 2 + cardSpacing / 2;

      for (let j = rowStart; j < rowEnd; j++) {
        const card = draw.neededCards[j]!;
        const suitInfo = SUIT_DISPLAY[card.suit] ?? { glyph: '?', color: 0xffffff };

        const cardText = new Text({
          text: `${card.rank}${suitInfo.glyph}`,
          style: {
            fontFamily: 'Arial, sans-serif',
            fontSize: 8,
            fill: suitInfo.color === 0x222222 ? 0xcccccc : suitInfo.color,
            align: 'center',
          },
        });
        cardText.anchor.set(0.5, 0);
        cardText.x = startX + (j - rowStart) * cardSpacing;
        cardText.y = currentY;
        container.addChild(cardText);
      }
      currentY += 11;
    }
  }

  /** Hide and clear the overlay */
  clear(): void {
    this.visible = false;
    for (const container of this.drawContainers) {
      container.destroy({ children: true });
    }
    this.drawContainers = [];
    this.background.clear();
  }

  override destroy(options?: boolean | { children?: boolean }): void {
    this.clear();
    super.destroy(options);
  }
}
